import * as Slider from '@radix-ui/react-slider'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import {
  AmountInputBase,
  Separator as Separator_,
  SliderIcon,
} from '@root/shared/ui-kit'

import { TradingSliderTicks } from './trading-slider-ticks'

import type { TradeType } from '@root/entities/trade-type'

type Props = {
  onChange: (value: string) => void
  value: number
  // slider
  type: TradeType.SwapDirection
  themeType?: TradeType.SwapDirection
  ticks: [number, number, number, number, number]
  forceTicksSign?: boolean
  step?: number

  // input
  leftContent: React.ReactNode
  rightContent: React.ReactNode
  decimalScale?: number
  suffix?: string
  prefix?: string
  placeholder?: string
}

export const TradingInputSlider: React.FC<Props> = ({
  type,
  value,
  ticks,
  onChange,
  themeType = type,
  prefix,
  leftContent,
  rightContent,
  decimalScale = 2,
  step = 1,
  suffix = '%',
  placeholder = '0%',
  forceTicksSign = false,
}) => {
  const [overTrack, setOverTrack] = useState(false)

  const min = ticks[0]
  const max = ticks[4]

  const percentage = Number(value) || 0
  const trackPosition = percentage - min

  return (
    <Root data-type={themeType}>
      <Group>
        <InputWithTrack>
          <Input>
            <Prefix>{leftContent}</Prefix>
            <Separator vertical />
            <InputBase
              prefix={prefix}
              suffix={suffix}
              decimalScale={decimalScale}
              placeholder={placeholder}
              value={value.toString()}
              onChange={(event) => {
                // input string, no special casting
                onChange(event.target.value)
              }}
            />
            <Suffix>{rightContent}</Suffix>
          </Input>
          <TrackClickArea>
            <Track aria-busy={overTrack}>
              <Range $percentage={`${trackPosition}%`} />
            </Track>
          </TrackClickArea>
        </InputWithTrack>
        <SliderRoot
          value={[percentage]}
          min={min}
          max={max}
          step={step}
          onValueChange={(value) => onChange(value.toString())}
        >
          <SliderTrack
            onMouseEnter={() => setOverTrack(true)}
            onMouseLeave={() => setOverTrack(false)}
          >
            <SliderRange />
          </SliderTrack>
          <SliderThumb>
            <SliderIcon size={16} />
          </SliderThumb>
        </SliderRoot>
      </Group>

      <TradingSliderTicks
        type={type}
        value={percentage}
        ticks={ticks}
        forceSign={forceTicksSign}
        onClick={(value) => onChange(value.toString())}
        css={css`
          margin-top: 14px;
          padding-inline: 5px;
        `}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  will-change: transform;

  &[data-type='buy'] {
    --slider-track: rgba(var(--upAccent-7-rgb), 0.4);
    --slider-range: var(--upAccent-9);
    --slider-thumb: var(--upAccent-9);
    --slider-active: var(--upAccent-9);
  }

  &[data-type='sell'] {
    --slider-track: rgba(var(--downAccent-7-rgb), 0.4);
    --slider-range: var(--downAccent-9);
    --slider-thumb: var(--downAccent-9);
    --slider-active: var(--downAccent-9);
  }
`

const Group = styled.div`
  display: grid;
  grid-template-rows: auto auto;
`

const InputWithTrack = styled.div`
  display: grid;
  position: relative;

  background: var(--appearanceA-7);
  border-radius: 10px;
  overflow: hidden;
`

const Input = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: 58px 30px minmax(0, 1fr) 30px;

  height: 38px;
  padding: 8px 10px 6px 10px;

  align-items: center;
`

const InputBase = styled(AmountInputBase)`
  background: transparent;
  color: var(--text-12);

  border: none;
  outline: none;
  height: 100%;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`

const Prefix = styled.span`
  color: var(--text-11);

  font-size: 12px;
  font-weight: 400;
  line-height: 20px;

  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Suffix = styled.span``

const Separator = styled(Separator_)`
  margin-inline-start: 15px;
`

const SliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
`

const TrackClickArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -9px;
  cursor: pointer;
  height: 20px;
  align-items: center;
  display: grid;
`

const SliderTrack = styled(Slider.Track)`
  background-color: transparent;
  position: absolute;
  flex-grow: 1;
  cursor: pointer;
  height: 15px;
  top: -8px;
  left: 0;
  right: 0;

  will-change: transform;
`

const SliderRange = styled(Slider.Range)``

const Track = styled.div`
  background-color: var(--slider-track);
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 4%);

  position: relative;
  flex-grow: 1;
  border-radius: 9999px;
  height: 2px;

  transition: height 150ms;

  &[aria-busy='true'] {
    height: 4px;
  }
`

const Range = styled.div.attrs<{ $percentage: string }>((x) => ({
  style: {
    width: x.$percentage,
  },
}))`
  background-color: var(--slider-range);

  position: absolute;
  border-radius: 9999px;
  height: 100%;
`

const SliderThumb = styled(Slider.Thumb)`
  color: var(--slider-thumb);

  cursor: pointer;
  display: block;

  padding-top: 4px;

  transition: all 150ms;

  &:hover {
    color: var(--slider-active);
    outline: none;
    transform: scale(1.5);
  }
  &:focus {
    color: var(--slider-active);
  }

  will-change: transform, color;
`
